import clsx from 'clsx';
import { useAtomValue } from 'jotai';
import { type ReactElement, useCallback, useState } from 'react';
import { toast } from 'sonner';

import Button from '@/components/Button/Button';
import type { TabItem } from '@/components/Tabs/Tabs';
import Tabs from '@/components/Tabs/Tabs';
import SetCardDetailsPanel from '@/features/sets/components/SetCardDetailsPanel';
import type { MinimalCard } from '@/services/cardDatabase';

import { useDraftActions } from '../hooks/useDraftActions';
import { draftStatsAtom } from '../state/draftAtoms';
import DraftCardGrid from './DraftCardGrid';
import DraftStatsPanel from './DraftStatsPanel';
import PickTimeline from './PickTimeline';

type ReviewTab = 'pool' | 'timeline' | 'stats';
type PoolSort = 'color' | 'cmc' | 'type' | 'pick-order';

const REVIEW_TABS: TabItem<ReviewTab>[] = [
  { id: 'pool', label: 'Pool' },
  { id: 'timeline', label: 'Timeline' },
  { id: 'stats', label: 'Stats' },
];

const COLOR_ORDER: Record<string, number> = { W: 0, U: 1, B: 2, R: 3, G: 4 };

function sortPool(cards: MinimalCard[], sort: PoolSort): MinimalCard[] {
  const sorted = [...cards];
  switch (sort) {
    case 'color':
      sorted.sort((a, b) => {
        const aKey = a.color_identity.length === 0 ? 6 : a.color_identity.length > 1 ? 5 : (COLOR_ORDER[a.color_identity[0]] ?? 6);
        const bKey = b.color_identity.length === 0 ? 6 : b.color_identity.length > 1 ? 5 : (COLOR_ORDER[b.color_identity[0]] ?? 6);
        return aKey - bKey || (a.cmc ?? 0) - (b.cmc ?? 0);
      });
      break;
    case 'cmc':
      sorted.sort((a, b) => (a.cmc ?? 0) - (b.cmc ?? 0));
      break;
    case 'type':
      sorted.sort((a, b) => (a.type_line ?? '').localeCompare(b.type_line ?? ''));
      break;
    case 'pick-order':
      // Already in pick order
      break;
  }
  return sorted;
}

export default function DraftReview(): ReactElement {
  const { session, resetDraft } = useDraftActions();
  const stats = useAtomValue(draftStatsAtom);
  const [activeTab, setActiveTab] = useState<ReviewTab>('pool');
  const [poolSort, setPoolSort] = useState<PoolSort>('color');
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);

  const handleSelectCard = useCallback((id: string) => setSelectedCardId(id), []);
  const handleClosePanel = useCallback(() => setSelectedCardId(null), []);

  if (!session) return <div />;

  const sortedPool = sortPool(session.playerPool, poolSort);

  const handleExport = async () => {
    // Deduplicate by scryfall ID and sum quantities
    const cardCounts = new Map<string, { card: MinimalCard; count: number }>();
    for (const card of session.playerPool) {
      const existing = cardCounts.get(card.id);
      if (existing) {
        existing.count++;
      } else {
        cardCounts.set(card.id, { card, count: 1 });
      }
    }

    const lines = Array.from(cardCounts.values()).map(
      ({ card, count }) => `${count} ${card.name} (${card.set.toUpperCase()}) ${card.collector_number}`,
    );
    const text = lines.join('\n');
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`Copied ${cardCounts.size} unique cards to clipboard`);
    } catch {
      toast.error('Failed to copy to clipboard');
    }
  };

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-zinc-700/50 bg-zinc-800/50 px-4 py-3">
        <div>
          <h2 className="text-lg font-bold text-zinc-100">Draft Complete</h2>
          <p className="text-sm text-zinc-400">
            {session.setName} · {session.playerPool.length} cards drafted
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="filled" color="primary" size="small" text="Copy to Clipboard" onClick={handleExport} />
          <Button variant="outlined" color="secondary" size="small" text="New Draft" onClick={resetDraft} />
        </div>
      </div>

      {/* Tabs */}
      <Tabs tabs={REVIEW_TABS} activeId={activeTab} onChange={setActiveTab} label="Draft review section" className="px-4" />

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto p-4">
        {activeTab === 'pool' && (
          <div className="space-y-4">
            {/* Sort controls */}
            <div className="flex gap-1">
              {(['color', 'cmc', 'type', 'pick-order'] as PoolSort[]).map((mode) => (
                <button
                  key={mode}
                  onClick={() => setPoolSort(mode)}
                  className={clsx(
                    'rounded px-2.5 py-1 text-xs font-medium capitalize transition-colors',
                    poolSort === mode ? 'bg-zinc-600 text-zinc-100' : 'text-zinc-400 hover:bg-zinc-700/50 hover:text-zinc-200',
                  )}
                >
                  {mode.replace('-', ' ')}
                </button>
              ))}
            </div>

            {/* Pool grid */}
            <DraftCardGrid cards={sortedPool} onCardClick={handleSelectCard} className="pt-2" />
          </div>
        )}

        {activeTab === 'timeline' && <PickTimeline picks={session.playerPicks} />}

        {activeTab === 'stats' && (
          <div className="mx-auto max-w-sm">
            <DraftStatsPanel stats={stats} />
          </div>
        )}
      </div>

      <SetCardDetailsPanel selectedCardId={selectedCardId} setName="Draft Pool" setCode={session.setCode} onClose={handleClosePanel} />
    </div>
  );
}
